import React from 'react';
import { MapPin, Phone, Mail, Clock } from 'lucide-react';
import { businessInfo, openingHours } from '../mockData';

export const Footer = () => {
  const currentYear = new Date().getFullYear();

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <footer className="bg-gray-900 text-gray-300">
      {/* Main Footer Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-12">
          {/* Company Info */}
          <div>
            <h3 className="text-white text-xl font-bold mb-4">
              M.A. Zulassungsdienst
            </h3>
            <p className="text-gray-400 leading-relaxed mb-4">
              Ihr zuverlässiger Partner für KFZ-Zulassungen in Velbert. Schnell, professionell und ohne Wartezeiten.
            </p>
            <div className="flex items-center space-x-2">
              <div className="flex text-yellow-400">
                {[...Array(5)].map((_, i) => (
                  <span key={i}>★</span>
                ))}
              </div>
              <span className="text-sm font-semibold text-white">
                {businessInfo.rating} / 5
              </span>
            </div>
          </div>

          {/* Contact */}
          <div>
            <h4 className="text-white font-bold mb-4">Kontakt</h4>
            <ul className="space-y-3">
              <li>
                <a 
                  href={`tel:${businessInfo.phone}`}
                  className="flex items-center space-x-3 hover:text-blue-400 transition-colors"
                >
                  <Phone className="w-4 h-4 flex-shrink-0" />
                  <span>{businessInfo.phone}</span>
                </a>
              </li>
              <li>
                <a 
                  href={`https://wa.me/${businessInfo.whatsappNumber}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center space-x-3 hover:text-blue-400 transition-colors"
                >
                  <svg className="w-4 h-4 flex-shrink-0" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
                  </svg>
                  <span>WhatsApp</span>
                </a>
              </li>
              <li className="flex items-start space-x-3">
                <MapPin className="w-4 h-4 flex-shrink-0 mt-1" />
                <span>
                  {businessInfo.address.street}<br />
                  {businessInfo.address.zip} {businessInfo.address.city}
                </span>
              </li>
            </ul>
          </div>

          {/* Opening Hours */}
          <div>
            <h4 className="text-white font-bold mb-4">Öffnungszeiten</h4>
            <ul className="space-y-2">
              {openingHours.map((schedule, index) => (
                <li key={index} className="text-sm">
                  <div className="flex flex-col">
                    <span className="font-semibold text-gray-200">{schedule.day}</span>
                    <span className="text-gray-400">{schedule.hours}</span>
                  </div>
                </li>
              ))}
            </ul>
          </div>

          {/* Services Quick Links */}
          <div>
            <h4 className="text-white font-bold mb-4">Unsere Leistungen</h4>
            <ul className="space-y-2">
              <li>
                <button 
                  onClick={() => document.getElementById('services')?.scrollIntoView({ behavior: 'smooth' })}
                  className="text-gray-400 hover:text-blue-400 transition-colors text-sm"
                >
                  Anmeldung & Ummeldung
                </button>
              </li>
              <li>
                <button 
                  onClick={() => document.getElementById('services')?.scrollIntoView({ behavior: 'smooth' })}
                  className="text-gray-400 hover:text-blue-400 transition-colors text-sm"
                >
                  Abmeldung
                </button>
              </li>
              <li>
                <button 
                  onClick={() => document.getElementById('services')?.scrollIntoView({ behavior: 'smooth' })}
                  className="text-gray-400 hover:text-blue-400 transition-colors text-sm"
                >
                  Wunschkennzeichen
                </button>
              </li>
              <li>
                <button 
                  onClick={() => document.getElementById('services')?.scrollIntoView({ behavior: 'smooth' })}
                  className="text-gray-400 hover:text-blue-400 transition-colors text-sm"
                >
                  Dokumentenprüfung
                </button>
              </li>
              <li>
                <button 
                  onClick={() => document.getElementById('services')?.scrollIntoView({ behavior: 'smooth' })}
                  className="text-gray-400 hover:text-blue-400 transition-colors text-sm"
                >
                  Versicherungsberatung
                </button>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          {/* Legal Disclaimer */}
          <div className="mb-4 pb-4 border-b border-gray-800">
            <p className="text-xs text-gray-500 text-center leading-relaxed">
              <span className="inline-flex items-center">
                <svg xmlns="http://www.w3.org/2000/svg" className="w-3 h-3 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <strong>Hinweis:</strong>
              </span>
              {' '}Wir sind ein privater Zulassungsdienst und stehen in keiner Verbindung zu einer Zulassungsbehörde. Die Abwicklung erfolgt über die zuständigen Stellen.
            </p>
          </div>

          <div className="flex flex-col sm:flex-row justify-between items-center space-y-4 sm:space-y-0">
            <p className="text-sm text-gray-400 text-center sm:text-left">
              © {currentYear} M.A. Zulassungsdienst. Alle Rechte vorbehalten.
            </p>
            <div className="flex items-center space-x-6">
              <a href="/impressum" className="text-sm text-gray-400 hover:text-blue-400 transition-colors">
                Impressum
              </a>
              <a href="/datenschutz" className="text-sm text-gray-400 hover:text-blue-400 transition-colors">
                Datenschutz
              </a>
              <button 
                onClick={scrollToTop}
                className="text-sm text-gray-400 hover:text-blue-400 transition-colors"
              >
                Nach oben ↑
              </button>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};
